import type { Config } from "wagmi"
import { runInAction } from "mobx"
import { createActor, fromPromise, setup } from "xstate"

import type { IEventBus } from "@/domains/events"
import type { IWithdrawLock, WithdrawState } from "@/domains/lock-page/withdraw-store"
import { resetNftInteraction, withdrawTokensFromExpiredVeInteraction } from "@/domains/interactions"
import { ResetNftActionsAdapter } from "@/domains/interactions/reset-nft/reset-nft-actions-adapter"
import { WithdrawTokensFromExpiredVeActionsAdapter } from "@/domains/interactions/withdraw-tokens-from-expired-ve/withdraw-tokens-from-expired-ve-actions-adapter"
import { WithdrawStore } from "@/domains/lock-page/withdraw-store"
import { WalletConnectionStore } from "@/domains/wallet"

interface WithdrawOrchestratorContext {
  chainId: number
  store: WithdrawStore
  config: Config
  lock: IWithdrawLock | null
  eventBus: IEventBus
  voterAddress: `0x${string}`
  vestedAddress: `0x${string}`
  walletStore: WalletConnectionStore
}

export class WithdrawOrchestrator {
  public readonly store: WithdrawStore

  private actor: ReturnType<typeof createActor>
  private eventBus: IEventBus
  private currentNftId: number | null

  constructor(
    chainId: number,
    store: WithdrawStore,
    config: Config,
    lock: IWithdrawLock | null,
    eventBus: IEventBus,
    voterAddress: `0x${string}`,
    vestedAddress: `0x${string}`,
    walletStore: WalletConnectionStore,
  ) {
    this.store = store
    this.eventBus = eventBus
    this.currentNftId = lock?.id ?? null

    const context: WithdrawOrchestratorContext & { __orchestrator?: WithdrawOrchestrator } = {
      chainId,
      store,
      config,
      lock,
      eventBus,
      voterAddress,
      vestedAddress,
      walletStore,
      __orchestrator: this,
    }

    const machine = createWithdrawMachine(context)
    this.actor = createActor(machine)

    // Sync XState state with MobX store
    this.actor.subscribe((snapshot) => {
      runInAction(() => {
        this.store.setState(snapshot.value as WithdrawState)
      })
    })

    // Start the actor
    this.actor.start()
  }

  destroy(): void {
    // Stop the actor
    this.actor.stop()
  }

  // Public API methods

  startWithdrawFlow(): void {
    this.actor.send({ type: "START_WITHDRAW" })
  }

  retryReset(): void {
    this.store.clearResetError()
    this.actor.send({ type: "RETRY_RESET" })
  }

  retryWithdraw(): void {
    this.store.clearWithdrawError()
    this.actor.send({ type: "RETRY_WITHDRAW" })
  }

  executeReset(): void {
    this.actor.send({ type: "RESET" })
  }

  executeWithdraw(): void {
    this.actor.send({ type: "WITHDRAW" })
  }

  reset(): void {
    this.store.reset()
    // Send CANCEL to transition back to idle
    // Final states can accept events if they have handlers defined (we added CANCEL handlers to final states)
    this.actor.send({ type: "CANCEL" })
  }

  cancel(): void {
    this.store.reset()
    this.actor.send({ type: "CANCEL" })
  }

  updateLock(lock: IWithdrawLock | null): void {
    const previousNftId = this.currentNftId
    this.currentNftId = lock?.id ?? null

    // Update the lock in the store first
    runInAction(() => {
      this.store.setLock(lock)
    })

    // If the NFT ID changed, reset and restart the flow
    if (previousNftId !== null && previousNftId !== this.currentNftId) {
      // Reset to idle state
      this.reset()
      // Start the flow after a brief delay to allow state to settle
      if (lock) {
        setTimeout(() => {
          // Check both store state and actor snapshot to ensure we're in idle
          const actorState = this.actor.getSnapshot().value
          if (actorState === "idle" && this.store.state === "idle" && this.store.lock) {
            this.startWithdrawFlow()
          }
        }, 100)
      }
    } else if (lock) {
      // If we have a lock, check the current state and start if needed
      const actorState = this.actor.getSnapshot().value

      if (actorState === "idle" && this.store.state === "idle") {
        // If we're in idle state, start the flow immediately
        this.startWithdrawFlow()
      } else if (this.store.state === "checking-expired") {
        // If we're in checking-expired, trigger the check
        this.actor.send({ type: "CHECK_EXPIRED" })
      }
    }
  }
}

// XState v5 Machine Definition
function createWithdrawMachine(context: WithdrawOrchestratorContext) {
  return setup({
    actors: {
      checkExpired: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        // Add a small delay to allow UI updates
        await new Promise((resolve) => setTimeout(resolve, 500))

        return {
          hasExpired: lock.hasExpired,
        }
      }),

      checkResetRequired: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        // Add a small delay to allow UI updates
        await new Promise((resolve) => setTimeout(resolve, 500))

        return {
          needsResetToWithdraw: lock.needsResetToWithdraw,
        }
      }),

      executeReset: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        const onChainActions = new ResetNftActionsAdapter()

        // Get current account from wallet store (real-time, not stale)
        const account = context.walletStore.getConnectedAddress()

        if (!account) {
          throw new Error("Account not connected")
        }

        const result = await resetNftInteraction({
          account,
          chainId: context.chainId,
          config: context.config,
          emitter: undefined,
          voterAddress: context.voterAddress,
          tokenId: lock.id,
          eventBus: context.eventBus,
          onChainActions,
        })

        if (!result || !result.success) {
          const errorMessage =
            typeof result === "object" && result !== null && "error" in result
              ? String(result.error)
              : "Reset transaction failed"
          throw new Error(errorMessage)
        }

        runInAction(() => {
          if (result.success && result.tx) {
            context.store.setResetTxHash(result.tx)
          }
        })

        return result
      }),

      executeWithdraw: fromPromise(async () => {
        const lock = context.store.lock
        if (!lock) {
          throw new Error("Lock not found")
        }

        const onChainActions = new WithdrawTokensFromExpiredVeActionsAdapter()

        // Get current account from wallet store (real-time, not stale)
        const account = context.walletStore.getConnectedAddress()

        if (!account) {
          throw new Error("Account not connected")
        }

        const result = await withdrawTokensFromExpiredVeInteraction({
          account,
          chainId: context.chainId,
          config: context.config,
          emitter: undefined,
          tokenId: lock.id,
          vestedAddress: context.vestedAddress,
          eventBus: context.eventBus,
          onChainActions,
        })

        if (!result || !result.success) {
          const errorMessage =
            typeof result === "object" && result !== null && "error" in result
              ? String(result.error)
              : "Withdraw transaction failed"
          throw new Error(errorMessage)
        }

        runInAction(() => {
          if (result.success && result.tx) {
            context.store.setWithdrawTxHash(result.tx)
          }
        })

        return result
      }),
    },
    guards: {
      isExpired: () => {
        return context.store.lock?.hasExpired ?? false
      },
      isNotExpired: () => {
        return !context.store.lock?.hasExpired
      },
      resetRequired: () => {
        return context.store.lock?.needsResetToWithdraw ?? false
      },
      resetNotRequired: () => {
        return !context.store.lock?.needsResetToWithdraw
      },
    },
  }).createMachine({
    id: "withdraw",
    initial: "idle",
    on: {
      CANCEL: ".idle",
    },
    states: {
      idle: {
        on: {
          START_WITHDRAW: {
            target: "checking-expired",
            guard: () => {
              // Only transition if we have a lock
              return context.store.lock !== null
            },
          },
        },
      },
      "checking-expired": {
        invoke: {
          src: "checkExpired",
          onDone: [
            {
              target: "not-expired",
              guard: "isNotExpired",
            },
            {
              target: "expired",
              guard: "isExpired",
            },
          ],
          onError: {
            target: "checking-expired",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Failed to check expiration"
                // For checking-expired errors, we might want to retry automatically
                console.error("Error checking expiration:", errorMessage)
              })
            },
          },
        },
        on: {
          CHECK_EXPIRED: {
            target: "checking-expired",
          },
          START_WITHDRAW: {
            target: "checking-expired",
          },
        },
      },
      "not-expired": {
        // Not a final state - can be reset when switching NFTs
        on: {
          CANCEL: "idle",
        },
      },
      expired: {
        always: {
          target: "checking-reset-required",
        },
      },
      "checking-reset-required": {
        invoke: {
          src: "checkResetRequired",
          onDone: [
            {
              target: "withdraw-allowed",
              guard: "resetNotRequired",
            },
            {
              target: "reset-required",
              guard: "resetRequired",
            },
          ],
          onError: {
            target: "checking-reset-required",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error
                    ? (event as any).error.message
                    : "Failed to check reset requirement"
                console.error("Error checking reset requirement:", errorMessage)
              })
            },
          },
        },
      },
      "reset-required": {
        on: {
          RESET: "resetting",
          CANCEL: "idle",
        },
      },
      resetting: {
        invoke: {
          src: "executeReset",
          onDone: "withdraw-allowed",
          onError: {
            target: "reset-failed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Reset transaction failed"
                context.store.setResetError(errorMessage)
              })
            },
          },
        },
        on: {
          CANCEL: "idle",
        },
      },
      "reset-failed": {
        on: {
          RETRY_RESET: "resetting",
          CANCEL: "idle",
        },
      },
      "withdraw-allowed": {
        on: {
          WITHDRAW: "withdrawing",
          CANCEL: "idle",
        },
      },
      withdrawing: {
        invoke: {
          src: "executeWithdraw",
          onDone: "withdrawn",
          onError: {
            target: "withdraw-failed",
            actions: (_, event) => {
              runInAction(() => {
                const errorMessage =
                  (event as any)?.error instanceof Error ? (event as any).error.message : "Withdraw transaction failed"
                context.store.setWithdrawError(errorMessage)
              })
            },
          },
        },
        on: {
          CANCEL: "idle",
        },
      },
      "withdraw-failed": {
        on: {
          RETRY_WITHDRAW: "withdrawing",
          CANCEL: "idle",
        },
      },
      withdrawn: {
        // Not a final state - can be reset when switching NFTs
        on: {
          RESET: "idle",
          CANCEL: "idle",
        },
      },
    },
  })
}
